import React, { forwardRef, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import omit from 'lodash/omit';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import { composeRef } from 'rc-util/lib/ref';
import Icon from 'choerodon-ui/lib/icon';
import ReactResizeObserver from 'choerodon-ui/lib/_util/resizeObserver';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import Tooltip from '../../tooltip';
import TransButton from '../../_util/TransButton';
import EllipsisTooltip from './EllipsisTooltip';
import Ellipsis from './Ellipsis';
import useMergedConfig from '../hooks/useMergedConfig';
import Typography from '../Typography';
import { $l } from '../../locale-context';
import { isStyleSupport } from '../../_util/styleChecker';
function wrapperDecorations({ mark, code, underline, delete: del, strong, keyboard, italic }, content) {
    let currentContent = content;
    function wrap(needed, tag) {
        if (!needed)
            return;
        currentContent = React.createElement(tag, {}, currentContent);
    }
    wrap(strong, 'strong');
    wrap(underline, 'u');
    wrap(del, 'del');
    wrap(code, 'code');
    wrap(mark, 'mark');
    wrap(keyboard, 'kbd');
    wrap(italic, 'i');
    return currentContent;
}
function getNode(dom, defaultNode, needDom) {
    if (dom === true || dom === undefined) {
        return defaultNode;
    }
    return dom || (needDom && defaultNode);
}
function toList(val) {
    return Array.isArray(val) ? val : [val];
}
const ELLIPSIS_STR = '...';
const Base = forwardRef((props, ref) => {
    const { prefixCls: customizePrefixCls, className, style, type, disabled, children, ellipsis, copyable, component, title, suffixCls, ...restProps } = props;
    const { getProPrefixCls } = useContext(ConfigContext);
    const typographyRef = useRef(null);
    // ============================ MISC ============================
    const prefixCls = getProPrefixCls('typography', customizePrefixCls);
    const textProps = omit(restProps, [
        'mark',
        'code',
        'delete',
        'underline',
        'strong',
        'keyboard',
        'italic',
        'dataSet',
        'rowIndex',
        'colIndex',
        'suffixCls',
    ]);
    // ========================== Copyable ==========================
    const [enableCopy, copyConfig] = useMergedConfig(copyable);
    const [copied, setCopied] = useState(false);
    const copyIdRef = useRef();
    const cleanCopyId = () => {
        clearTimeout(copyIdRef.current);
    };
    const onCopyClick = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        copy(copyConfig.text || String(children) || '');
        setCopied(true);
        // Trigger tips update
        cleanCopyId();
        copyIdRef.current = setTimeout(() => {
            setCopied(false);
        }, 3000);
        if (copyConfig.onCopy) {
            copyConfig.onCopy();
        }
    };
    useEffect(() => cleanCopyId, []);
    // ========================== Ellipsis ==========================
    const [isLineClampSupport, setIsLineClampSupport] = useState(false);
    const [isTextOverflowSupport, setIsTextOverflowSupport] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [isJsEllipsis, setIsJsEllipsis] = useState(false);
    const [isNativeEllipsis, setIsNativeEllipsis] = useState(false);
    const [enableEllipsis, ellipsisConfig] = useMergedConfig(ellipsis, {
        expandable: false,
    });
    const mergedEnableEllipsis = enableEllipsis && !expanded;
    const { rows = 1 } = ellipsisConfig;
    const needMeasureEllipsis = useMemo(() => !mergedEnableEllipsis ||
        ellipsisConfig.suffix !== undefined ||
        ellipsisConfig.onEllipsis ||
        ellipsisConfig.expandable ||
        enableCopy, [mergedEnableEllipsis, ellipsisConfig, enableCopy]);
    useLayoutEffect(() => {
        if (enableEllipsis && !needMeasureEllipsis) {
            setIsLineClampSupport(isStyleSupport('webkitLineClamp'));
            setIsTextOverflowSupport(isStyleSupport('textOverflow'));
        }
    }, [needMeasureEllipsis, enableEllipsis]);
    const cssEllipsis = useMemo(() => {
        if (needMeasureEllipsis) {
            return false;
        }
        if (rows === 1) {
            return isTextOverflowSupport;
        }
        return isLineClampSupport;
    }, [needMeasureEllipsis, isTextOverflowSupport, isLineClampSupport]);
    const isMergedEllipsis = mergedEnableEllipsis && (cssEllipsis ? isNativeEllipsis : isJsEllipsis);
    const cssTextOverflow = mergedEnableEllipsis && rows === 1 && cssEllipsis;
    const cssLineClamp = mergedEnableEllipsis && rows > 1 && cssEllipsis;
    // >>>>> Expand
    const onExpandClick = e => {
        setExpanded(true);
        if (ellipsisConfig.onExpand) {
            ellipsisConfig.onExpand(e);
        }
    };
    const [ellipsisWidth, setEllipsisWidth] = useState(0);
    const onResize = (width) => {
        setEllipsisWidth(width);
    };
    const onJsEllipsis = (jsEllipsis) => {
        setIsJsEllipsis(jsEllipsis);
        if (isJsEllipsis !== jsEllipsis && ellipsisConfig.onEllipsis) {
            ellipsisConfig.onEllipsis(jsEllipsis);
        }
    };
    useEffect(() => {
        const textEle = typographyRef.current;
        if (enableEllipsis && cssEllipsis && textEle) {
            const currentEllipsis = cssLineClamp
                ? textEle.offsetHeight < textEle.scrollHeight
                : textEle.offsetWidth < textEle.scrollWidth;
            if (isNativeEllipsis !== currentEllipsis) {
                setIsNativeEllipsis(currentEllipsis);
            }
        }
    }, [enableEllipsis, cssEllipsis, children, cssLineClamp]);
    // ========================== Tooltip ===========================
    const tooltipTitle = ellipsisConfig.tooltip === true ? children : ellipsisConfig.tooltip;
    const topAriaLabel = useMemo(() => {
        const isValid = (val) => ['string', 'number'].includes(typeof val);
        if (!enableEllipsis || cssEllipsis) {
            return undefined;
        }
        if (isValid(children)) {
            return children;
        }
        if (isValid(title)) {
            return title;
        }
        if (isValid(tooltipTitle)) {
            return tooltipTitle;
        }
        return undefined;
    }, [enableEllipsis, cssEllipsis, title, tooltipTitle, isMergedEllipsis]);
    // =========================== Render ===========================
    // >>>>>>>>>>> Typography
    // Expand
    const renderExpand = () => {
        const { expandable, symbol } = ellipsisConfig;
        if (!expandable)
            return null;
        let expandContent;
        if (symbol) {
            expandContent = symbol;
        }
        else {
            expandContent = $l('Typography', 'expand');
        }
        return (React.createElement("a", { key: "expand", className: `${prefixCls}-expand`, onClick: onExpandClick, "aria-label": $l('Typography', 'expand') }, expandContent));
    };
    // Copy
    const renderCopy = () => {
        if (!enableCopy)
            return;
        const { tooltips, icon } = copyConfig;
        const tooltipNodes = toList(tooltips);
        const iconNodes = toList(icon);
        const copyTitle = copied
            ? getNode(tooltipNodes[1], $l('Typography', 'copied'))
            : getNode(tooltipNodes[0], $l('Typography', 'copy'));
        const systemStr = copied ? $l('Typography', 'copied') : $l('Typography', 'copy');
        const ariaLabel = typeof copyTitle === 'string' ? copyTitle : systemStr;
        return (React.createElement(Tooltip, { key: "copy", title: copyTitle },
            React.createElement(TransButton, { className: classNames(`${prefixCls}-copy`, copied && `${prefixCls}-copy-success`), onClick: onCopyClick, "aria-label": ariaLabel }, copied
                ? getNode(iconNodes[1], React.createElement(Icon, { type: "check" }), true)
                : getNode(iconNodes[0], React.createElement(Icon, { type: "content_copy" }), true))));
    };
    const renderOperations = (renderExpanded) => [
        renderExpanded && renderExpand(),
        renderCopy(),
    ];
    const renderEllipsis = (needEllipsis) => [
        needEllipsis && (React.createElement("span", { "aria-hidden": true, key: "ellipsis" }, ELLIPSIS_STR)),
        ellipsisConfig.suffix,
        renderOperations(needEllipsis),
    ];
    return (React.createElement(ReactResizeObserver, { onResize: onResize, disabled: !mergedEnableEllipsis || cssEllipsis },
        React.createElement(EllipsisTooltip, { title: tooltipTitle, enabledEllipsis: mergedEnableEllipsis, isEllipsis: isMergedEllipsis },
            React.createElement(Typography, Object.assign({ className: classNames({
                    [`${prefixCls}-${type}`]: type,
                    [`${prefixCls}-${suffixCls}`]: suffixCls,
                    [`${prefixCls}-disabled`]: disabled,
                    [`${prefixCls}-ellipsis`]: enableEllipsis,
                    [`${prefixCls}-single-line`]: mergedEnableEllipsis && rows === 1,
                    [`${prefixCls}-ellipsis-single-line`]: cssTextOverflow,
                    [`${prefixCls}-ellipsis-multiple-line`]: cssLineClamp,
                }, className), style: {
                    ...style,
                    WebkitLineClamp: cssLineClamp ? rows : undefined,
                }, component: component, ref: composeRef(typographyRef, ref), "aria-label": topAriaLabel, title: title }, textProps),
                React.createElement(Ellipsis, { enabledMeasure: mergedEnableEllipsis && !cssEllipsis, text: children, rows: rows, width: ellipsisWidth, onEllipsis: onJsEllipsis }, (node, needEllipsis) => {
                    let renderNode = node;
                    if (node.length && needEllipsis && topAriaLabel) {
                        renderNode = (React.createElement("span", { key: "show-content", "aria-hidden": true }, renderNode));
                    }
                    const wrappedContext = wrapperDecorations(props, React.createElement(React.Fragment, null,
                        renderNode,
                        renderEllipsis(needEllipsis)));
                    return wrappedContext;
                })))));
});
Base.displayName = 'Base';
Base._PRO_TYPOGRAPHY_BASE = true;
Base.defaultProps = {
    component: 'div',
};
export default Base;
